import type { DesktopAudible } from '@amzn/bf-playback'
import { routeAudibleGateway } from '@amzn/bf-playback/web'
import { DeviceType } from '@amazon/vinyl-util'
import { getDeviceConfig } from '@/device/deviceConfig'
import { nativeEnginePlaysShaka, nativePlaybackInstalled } from '@/downloads/nativePlayback'
import { musicTld } from '@/territory'

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
    const tauri = await import('@tauri-apps/api/core')
    return tauri.invoke<T>(command, args)
}

/**
 * Whether the desktop app plays audiobooks: on a native engine that hosts the library's player,
 * in a macOS shell whose Audible forwarder has a signed-in device. Read on every call, since the
 * device signs in and out.
 */
export async function desktopAudibleAvailable(): Promise<boolean> {
    if (!nativePlaybackInstalled() || !nativeEnginePlaysShaka()) return false
    return invoke<boolean>('audible_available')
}

/**
 * The desktop app's Audible (`packages/bf-tauri/src-tauri/src/audible.rs`): the library plays on
 * the native engine, and the shell forwards its requests with the shell's cookies. Undefined
 * anywhere else.
 */
export function desktopAudible(): DesktopAudible | undefined {
    const config = getDeviceConfig()
    if (!config.installed || config.deviceType !== DeviceType.DESKTOP) return undefined
    return {
        openSession: async (territory) => {
            const session = await invoke<{ baseUrl: string; csrf: string | null }>(
                'audible_open_session',
                { tld: musicTld(territory), mode: 'cookie' }
            )
            return { baseUrl: routeAudibleGateway(session.baseUrl), csrf: session.csrf ?? '' }
        },
        // The engine's own `Audio`, which the native shim installs in place of the webview's.
        createAudioElement: () => new Audio(),
    }
}
